import { obligationStateLabel, ObligationState } from '@kanos/domain';
import { Button, cx, Skeleton, useToast } from '@kanos/ui';
import { formatDate, Page, ProblemAlert } from '@kanos/web-core';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Bell, ChevronRight, CircleAlert, CircleHelp } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { z } from 'zod';
import { useFeature } from '../firm/feature-gate';
import { TodayFollowUps } from '../firm/follow-ups';
import { staleText } from '../firm/obligations-card';
import { SetupGlance } from '../firm/setup-glance';
import { obligationsQuery, openTasksQuery, useCloseNotice, type Task } from '../firm/queries';
import { glanceLink } from '../glance';
import { markTourSeen, TourDialog, tourSeen } from '../help/tour';
import { useMe } from '../me';
import { engagementOverviewQuery } from '../firm/engagement-queries';
import { StartIntakeButton } from '../firm/start-intake';
import { TodayFees } from '../today/today-fees';
import { TodayFunnel } from '../today/today-funnel';
import { SoonRows, soonDates } from '../today/today-soon';
import { TodayTimeline } from '../today/today-timeline';

/**
 * `?tour=1` opens the tour - from the help menu, wherever one is. The former `?rundgang=1` still
 * opens it, so links kept from before keep working.
 */
const Search = z.object({
  tour: z.literal(1).optional().catch(undefined),
  rundgang: z.literal(1).optional().catch(undefined),
});

export const Route = createFileRoute('/')({
  validateSearch: Search,
  component: Today,
});

type OpenTasks = UseQueryResult<{ tasks: Task[]; more: boolean }>;

const count = new Intl.NumberFormat('de-DE');

/** The mark of a task: a question to clarify, or a notice of something Kanos did. */
function TaskIcon({ task }: { task: Task }) {
  const Icon = task.kind === 'clarification' ? CircleHelp : Bell;
  return (
    <span
      aria-hidden
      className={cx(
        'grid size-11 shrink-0 place-items-center rounded-full',
        task.kind === 'clarification'
          ? 'bg-accent-soft text-accent-text'
          : 'bg-warning-bg text-warning-fg',
      )}
    >
      <Icon className="size-5" />
    </span>
  );
}

function TaskText({ task, detail }: { task: Task; detail: string }) {
  return (
    // At least 12rem wide: below that the action wraps under the text instead of squeezing it.
    <span className="min-w-0 flex-[1_1_12rem]">
      <span className="line-clamp-2 font-semibold">{task.title}</span>
      <span className="block truncate text-sm text-muted">
        {detail} {formatDate(task.createdAt)}
      </span>
    </span>
  );
}

/** A question the rules could not answer: answered where the obligations are. */
function ClarificationRow({ task }: { task: Task }) {
  return (
    <Link
      to="/obligations"
      className="flex items-center gap-4 rounded-sm px-3 py-3.5 outline-none transition-colors hover:bg-fill focus-visible:outline-2 focus-visible:outline-focus"
    >
      <TaskIcon task={task} />
      <TaskText task={task} detail="Zu klären · offen seit" />
      <span className="inline-flex shrink-0 items-center gap-1 text-[0.9375rem] font-semibold">
        {/* Phones: the arrow says it; the words stay for screen readers. */}
        <span className="max-sm:sr-only">Klären</span>
        <ChevronRight aria-hidden className="size-4" />
      </span>
    </Link>
  );
}

/** A notice: an administrator takes note of it right here; everyone else sees it waiting. */
function NoticeRow({ task, firmAdmin }: { task: Task; firmAdmin: boolean }) {
  const me = useMe();
  const close = useCloseNotice(me.activeTenantId);
  const toast = useToast();
  return (
    <div className="grid gap-2 px-3 py-3.5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <TaskIcon task={task} />
        <TaskText task={task} detail="Hinweis · am" />
        {firmAdmin ? (
          <Button
            variant="secondary"
            size="sm"
            className="ml-15 sm:ml-0"
            isPending={close.isPending}
            onPress={() =>
              close.mutate(task.id, {
                onSuccess: () => toast({ tone: 'success', title: 'Hinweis zur Kenntnis genommen' }),
              })
            }
          >
            Zur Kenntnis genommen<span className="sr-only">: {task.title}</span>
          </Button>
        ) : (
          <span className="ml-15 text-sm text-muted sm:ml-0">Für Admins der Kanzlei</span>
        )}
      </div>
      {close.error ? <ProblemAlert error={close.error} /> : null}
    </div>
  );
}

/**
 * Everything that waits for a person of the firm: contract dates of the next days first, then
 * questions and notices, oldest first.
 */
function NeedsYou({ query, soon }: { query: OpenTasks; soon?: ReactNode }) {
  const me = useMe();
  if (query.isError) return null;
  return (
    <section
      aria-labelledby="needs-you"
      className="glass min-w-0 self-start rounded-lg px-2 py-2 sm:px-3"
    >
      <div className="flex items-center justify-between gap-3 px-3 pt-3 pb-1">
        <h2 id="needs-you" className="text-lg font-semibold tracking-[-0.02em]">
          Als Nächstes
        </h2>
        {query.data && query.data.tasks.length > 0 && (
          <span className="rounded-full bg-ink px-2.5 py-0.5 text-xs font-semibold text-ink-contrast tabular-nums">
            {count.format(query.data.tasks.length)}
            {query.data.more && '+'}
            <span className="sr-only"> offene Aufgaben</span>
          </span>
        )}
      </div>
      {soon}
      {query.isPending ? (
        <div aria-busy className="grid gap-2 px-3 py-3" role="status" aria-label="Wird geladen">
          <Skeleton className="h-11" />
          <Skeleton className="h-11" />
        </div>
      ) : query.data.tasks.length === 0 ? (
        soon ? null : (
          <p className="px-3 pt-1 pb-4 text-[0.9375rem] text-muted">
            Gerade wartet nichts auf Sie. Neue Fragen und Hinweise erscheinen hier von selbst.
          </p>
        )
      ) : (
        <ul>
          {query.data.tasks.map((task, i) => (
            <li key={task.id} className={cx(i > 0 && 'border-t border-hairline')}>
              {task.kind === 'clarification' ? (
                <ClarificationRow task={task} />
              ) : (
                <NoticeRow task={task} firmAdmin={me.firmAdmin} />
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** How far the obligation catalogue got for the firm, in one line: per state, as last derived. */
function ObligationsGlance() {
  const me = useMe();
  const obligations = useQuery(obligationsQuery(me.activeTenantId));
  const assessment = obligations.data?.assessment;
  let body: ReactNode;
  if (obligations.isPending) {
    body = <Skeleton className="h-5" />;
  } else if (obligations.isError) {
    body = <ProblemAlert error={obligations.error} />;
  } else if (!assessment) {
    body = (
      <p className="text-sm text-muted">
        Noch nicht ermittelt – das geschieht, sobald die Basisdaten der Kanzlei stehen.
      </p>
    );
  } else {
    const counts = ObligationState.options
      .map((state) => ({
        state,
        n: assessment.obligations.filter((o) => o.state === state).length,
      }))
      .filter(({ n }) => n > 0);
    body = (
      <>
        <ul className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
          {counts.map(({ state, n }) => (
            <li key={state} className="inline-flex items-center gap-1.5">
              {state === 'evaluation_error' && (
                <CircleAlert aria-hidden className="size-4 text-danger-fg" />
              )}
              <b className="tabular-nums">{count.format(n)}</b> {obligationStateLabel[state]}
            </li>
          ))}
        </ul>
        <p className="text-sm text-muted">
          {obligations.data.stale.length > 0
            ? staleText(obligations.data.stale, me.firmAdmin)
            : `Zuletzt ermittelt am ${formatDate(assessment.assessedAt)}`}
        </p>
      </>
    );
  }
  return (
    <section
      aria-labelledby="obligations-title"
      className="glass grid content-start gap-2.5 rounded-lg p-5"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 id="obligations-title" className="text-lg font-semibold tracking-[-0.02em]">
          Pflichten
        </h2>
        <Link to="/obligations" className={glanceLink}>
          Ansehen
          <ChevronRight aria-hidden className="size-4" />
        </Link>
      </div>
      {body}
    </section>
  );
}

const longDate = new Intl.DateTimeFormat('de-DE', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

/** „Guten Morgen, Martina.“ under the day - by the hour of the person's own clock. */
function Greeting({ firstName }: { firstName: string | null }) {
  const now = new Date();
  const hour = now.getHours();
  const hello = hour < 11 ? 'Guten Morgen' : hour < 18 ? 'Guten Tag' : 'Guten Abend';
  return (
    <div>
      <p className="text-[0.8125rem] font-semibold tracking-[0.04em] text-muted uppercase">
        {longDate.format(now)}
      </p>
      <p className="mt-1 text-[2rem] leading-tight font-semibold tracking-[-0.04em] sm:text-[2.5rem]">
        {hello}
        {firstName ? `, ${firstName}` : ''}.
      </p>
    </div>
  );
}

/** The tour on offer beside the page's title, until it was taken. */
function useTourOffer(start: () => void): (() => void) | null {
  const [offered, setOffered] = useState(() => !tourSeen());
  if (!offered) return null;
  return () => {
    setOffered(false);
    start();
  };
}

/** The cards under the day, one height in a row: set-up, follow-ups, obligations. */
function FirmCards({ portal }: { portal: boolean }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      <SetupGlance />
      {portal && <TodayFollowUps />}
      <ObligationsGlance />
    </div>
  );
}

/** With the portal: what the contracts come to, the way to a client, the next fourteen days. */
function PortalToday({ tasks }: { tasks: OpenTasks }) {
  const me = useMe();
  const overview = useQuery(engagementOverviewQuery(me.activeTenantId));
  if (overview.isPending) {
    return (
      <div aria-busy role="status" aria-label="Wird geladen" className="grid gap-6">
        <Skeleton className="h-56" />
        <Skeleton className="h-48" />
      </div>
    );
  }
  if (overview.isError) {
    return (
      <>
        <ProblemAlert error={overview.error} />
        <NeedsYou query={tasks} />
      </>
    );
  }
  const soon = soonDates(overview.data);
  return (
    <>
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)] lg:gap-10">
        <div className="grid min-w-0 content-start gap-8">
          <Greeting firstName={me.firstName} />
          <TodayFees overview={overview.data} />
        </div>
        <NeedsYou
          query={tasks}
          {...(soon.length > 0
            ? { soon: <SoonRows today={overview.data.today} soon={soon} /> }
            : {})}
        />
      </div>
      <TodayFunnel overview={overview.data} />
      <TodayTimeline overview={overview.data} />
    </>
  );
}

function Today() {
  const me = useMe();
  const tasks = useQuery(openTasksQuery(me.activeTenantId));
  const hasFeature = useFeature();
  const portal = hasFeature('check-point');
  const { tour, rundgang } = Route.useSearch();
  const navigate = useNavigate();
  const showTour = (open: boolean) => {
    if (open) markTourSeen();
    void navigate({ to: '/', search: open ? { tour: 1 } : {}, replace: !open });
  };
  const onTour = useTourOffer(() => showTour(true));
  const actions = (
    <>
      {onTour && (
        <Button variant="secondary" size="md" onPress={onTour}>
          Rundgang starten
        </Button>
      )}
      {portal && <StartIntakeButton size="md" />}
    </>
  );
  return (
    <Page title="Heute" actions={actions}>
      {(tour === 1 || rundgang === 1) && <TourDialog onClose={() => showTour(false)} />}
      <div className="grid gap-6">
        {portal ? (
          <PortalToday tasks={tasks} />
        ) : (
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)] lg:gap-10">
            <Greeting firstName={me.firstName} />
            <NeedsYou query={tasks} />
          </div>
        )}
        <FirmCards portal={portal} />
      </div>
    </Page>
  );
}
